import React, { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, Loader2, CheckCircle2, AlertTriangle, XCircle, Clock, CircleSlash, Layers, Upload } from "lucide-react";

// Shared building blocks for the Chain Test Lists workspace. Palette matches the
// chain pages (navy #0F4C81, teal #00A6A6, mint #2EBD85, borders #DCEAEA).

// contract: loan-and-subscription/vendor/models/vendor_chain_test_list.py UPLOAD_STATUS_*
export const TERMINAL_STATUSES = ["completed", "partial", "failed", "superseded"];
export const isProcessing = (upload) =>
  !!upload && !TERMINAL_STATUSES.includes(upload.status) && !upload.stuck;

export const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const STATUS_META = {
  queued: { label: "Queued", icon: Clock, cls: "bg-slate-50 text-slate-600 ring-slate-500/20", spin: false },
  processing: { label: "Processing", icon: Loader2, cls: "bg-sky-50 text-sky-700 ring-sky-600/20", spin: true },
  completed: { label: "Processed", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" },
  partial: { label: "Partially processed", icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  failed: { label: "Failed", icon: XCircle, cls: "bg-red-50 text-red-700 ring-red-600/20" },
  superseded: { label: "Superseded", icon: CircleSlash, cls: "bg-gray-100 text-gray-500 ring-gray-400/20" },
  stuck: { label: "Stuck", icon: AlertTriangle, cls: "bg-red-50 text-red-700 ring-red-600/20" },
};

export const UploadStatusPill = ({ upload }) => {
  if (!upload) return null;
  const key = upload.stuck ? "stuck" : upload.status;
  const meta = STATUS_META[key] || STATUS_META.queued;
  const Icon = meta.icon;
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={key}
        initial={{ opacity: 0, y: -2 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 2 }}
        transition={{ duration: 0.15 }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${meta.cls}`}
      >
        <Icon className={`w-3 h-3 ${meta.spin ? "animate-spin" : ""}`} />
        {meta.label}
      </motion.span>
    </AnimatePresence>
  );
};

export const SourcePill = ({ lab }) =>
  lab.test_source === "chain" ? (
    <span className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#E6F6F6] text-[#007C7C] ring-1 ring-inset ring-[#00A6A6]/25">
      <Layers className="w-3 h-3 shrink-0" />
      <span className="truncate">{lab.test_list?.name}</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-400/20 whitespace-nowrap">
      <Upload className="w-3 h-3" /> Direct upload
    </span>
  );

export const IndeterminateBar = () => {
  const reduce = useReducedMotion();
  return (
    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-[#E3F1F1]">
      {reduce ? (
        <div className="absolute inset-y-0 left-0 w-1/2 bg-[#00A6A6]/70 rounded-full" />
      ) : (
        <motion.div
          className="absolute inset-y-0 w-1/3 rounded-full bg-gradient-to-r from-[#00A6A6] to-[#2EBD85]"
          initial={{ x: "-100%" }}
          animate={{ x: "300%" }}
          transition={{ repeat: Infinity, duration: 1.3, ease: "easeInOut" }}
        />
      )}
    </div>
  );
};

export const ProgressBar = ({ value }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#E3F1F1]">
    <motion.div
      className="h-full rounded-full bg-gradient-to-r from-[#00A6A6] to-[#2EBD85]"
      initial={false}
      animate={{ width: `${Math.max(2, Math.min(100, value))}%` }}
      transition={{ duration: 0.2 }}
    />
  </div>
);

export const Skeleton = ({ className = "" }) => <div className={`rounded-md bg-gray-100 animate-pulse ${className}`} />;

export const Panel = ({ title, subtitle, action, children, className = "" }) => (
  <section className={`bg-white rounded-2xl border border-[#DCEAEA] shadow-[0_1px_2px_rgba(0,166,166,0.05)] overflow-hidden ${className}`}>
    <header className="px-5 py-4 border-b border-[#E9F2F2] bg-gradient-to-r from-[#F5FAFA] to-transparent flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-[#0F4C81]">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center text-center gap-2 px-6 py-12">
    <div className="w-12 h-12 rounded-2xl bg-[#F1F8F8] ring-1 ring-[#DCEAEA] flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#00A6A6]" />
    </div>
    <p className="text-sm font-semibold text-gray-800">{title}</p>
    {description && <p className="text-xs text-gray-500 max-w-sm">{description}</p>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

export const ErrorCard = ({ message, onRetry }) => (
  <div className="m-5 flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3">
    <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-sm text-red-700">{message}</p>
    </div>
    {onRetry && (
      <button type="button" onClick={onRetry} className="text-xs font-medium text-red-700 hover:text-red-900 underline underline-offset-2">
        Retry
      </button>
    )}
  </div>
);

export const PrimaryButton = ({ children, className = "", ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-white bg-[#0F4C81] hover:bg-[#0C3E6B] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
  >
    {children}
  </button>
);

export const SecondaryButton = ({ children, className = "", ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
  >
    {children}
  </button>
);

// Right-side drawer with a sticky footer. Esc closes unless `busy`.
export const Drawer = ({ open, onClose, title, subtitle, children, footer, busy = false, width = "max-w-xl" }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <motion.div
            className="absolute inset-0 bg-[#0B2A45]/30 backdrop-blur-[2px]"
            onClick={() => !busy && onClose()}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={`relative w-full ${width} h-full bg-[#F7FBFB] shadow-2xl flex flex-col`}
            initial={{ x: 32, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 32, opacity: 0 }}
            transition={{ type: "tween", duration: 0.2, ease: "easeOut" }}
          >
            <div className="px-6 py-4 bg-white border-b border-[#E9F2F2] flex items-start justify-between gap-3 shrink-0">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">{title}</h3>
                {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} disabled={busy} aria-label="Close"
                className="p-2 -mr-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-40 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">{children}</div>
            {footer && <div className="px-6 py-4 bg-white border-t border-[#E9F2F2] shrink-0">{footer}</div>}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

// Centered confirmation dialog.
export const Dialog = ({ open, onClose, icon: Icon, tone = "amber", title, children, actions, busy = false }) => {
  const toneCls = {
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    teal: "bg-[#E6F6F6] text-[#00A6A6]",
  }[tone];
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0B2A45]/35 backdrop-blur-[2px]"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}
        >
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-label={title}
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.98, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 6 }} transition={{ duration: 0.16 }}
          >
            <div className="px-6 py-5 flex items-start gap-3">
              {Icon && (
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${toneCls}`}>
                  <Icon className="w-5 h-5" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                <div className="mt-1.5 text-sm text-gray-600">{children}</div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">{actions}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
